import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import AsyncSelect from "react-select/async";
import Select from 'react-select';

// My components
import SecCard from "../../components/common/SecCard";
import Loader from "../../components/common/Loader";
import BackButton from "../../components/common/BackButton";

// My css
import css from "../../css/institute/add-insti-page.module.css";
import { SERVER_ORIGIN } from "../../utilities/constants";

const EditTermsAndConditions = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [allEMs, setAllEMs] = useState([]);
    const [selectedEM, setSelectedEM] = useState(null);
    
    const [termsForm, setTermsForm] = useState({
        title: "",
        content: "",
        version: "1.0",
        financialYear: "",
        emData: {
            chapterName: "",
            regionName: "",
            emName: "",
            emId: ""
        },
        effectiveDate: "",
        expiryDate: "",
        appliesTo: ['all'],
        tags: []
    });

    const [errors, setErrors] = useState({});
    const navigate = useNavigate();
    const { id } = useParams();

    // Financial year options (current year and next few years)
    const generateFinancialYears = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = -2; i <= 3; i++) {
            const startYear = currentYear + i;
            const endYear = startYear + 1;
            years.push({
                value: `${startYear}-${endYear.toString().slice(-2)}`,
                label: `${startYear}-${endYear.toString().slice(-2)}`
            });
        }
        return years;
    };

    const financialYearOptions = generateFinancialYears();

    const appliesToOptions = [
        { value: 'all', label: 'All Users' },
        { value: 'students', label: 'Students' },
        { value: 'institutes', label: 'Institutes' }
    ];

    useEffect(() => {
        fetchAllEMs();
        fetchTermsData();
    }, [id]);

    const fetchAllEMs = async () => {
        try {
            const response = await fetch(
                `${SERVER_ORIGIN}/api/em/auth/get-all?limit=1000`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": sessionStorage.getItem("token"),
                    },
                }
            );

            const result = await response.json();
            if (response.status === 200) {
                setAllEMs(result.filteredChapterEMs || []);
            }
        } catch (err) {
            console.error("Error fetching EMs:", err);
        }
    };

    const fetchTermsData = async () => {
        try {
            const response = await fetch(
                `${SERVER_ORIGIN}/api/terms/auth/${id}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": sessionStorage.getItem("token"),
                    },
                }
            );

            const result = await response.json();
            
            if (response.status === 200) {
                const data = result.data;
                
                // Format dates for input fields
                const formatDate = (dateString) => {
                    if (!dateString) return "";
                    return new Date(dateString).toISOString().split('T')[0];
                };

                setTermsForm({
                    title: data.title || "",
                    content: data.content || "",
                    version: data.version || "1.0",
                    financialYear: data.financialYear || "",
                    emData: data.emData || {
                        chapterName: "",
                        regionName: "",
                        emName: "",
                        emId: ""
                    },
                    effectiveDate: formatDate(data.effectiveDate),
                    expiryDate: formatDate(data.expiryDate),
                    appliesTo: data.appliesTo || ['all'],
                    tags: data.tags || []
                });

                // Set selected EM for the AsyncSelect
                if (data.emData && data.emData.emId) {
                    setSelectedEM({
                        value: data.emData.emId,
                        label: `${data.emData.emName} - ${data.emData.chapterName} (${data.emData.regionName})`,
                        emData: data.emData
                    });
                }
            } else {
                toast.error("Failed to fetch Terms and Conditions data");
                navigate("/terms-conditions/all");
            }
        } catch (err) {
            toast.error("Error fetching Terms and Conditions data");
            navigate("/terms-conditions/all");
        } finally {
            setIsLoading(false);
        }
    };

    const loadEMOptions = (inputValue, callback) => {
        const filteredOptions = allEMs
            .filter(em =>
                em.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                em.chapterName.toLowerCase().includes(inputValue.toLowerCase()) ||
                em.region.toLowerCase().includes(inputValue.toLowerCase())
            )
            .map(em => ({
                value: em._id,
                label: `${em.name} - ${em.chapterName} (${em.region})`,
                emData: {
                    chapterName: em.chapterName,
                    regionName: em.region,
                    emName: em.name,
                    emId: em._id
                }
            }));
        
        callback(filteredOptions);
    };

    const handleEMChange = (selectedOption) => {
        setSelectedEM(selectedOption);
        if (selectedOption) {
            setTermsForm({
                ...termsForm,
                emData: selectedOption.emData
            });
        } else {
            setTermsForm({
                ...termsForm,
                emData: {
                    chapterName: "",
                    regionName: "",
                    emName: "",
                    emId: ""
                }
            });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTermsForm({
            ...termsForm,
            [name]: value
        });
    };

    const handleFinancialYearChange = (selectedOption) => {
        setTermsForm({
            ...termsForm,
            financialYear: selectedOption ? selectedOption.value : ""
        });
    };

    const handleAppliesToChange = (selectedOptions) => {
        setTermsForm({
            ...termsForm,
            appliesTo: selectedOptions ? selectedOptions.map(option => option.value) : ['all']
        });
    };

    const handleTagsChange = (e) => {
        const tagsArray = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
        setTermsForm({
            ...termsForm,
            tags: tagsArray
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!termsForm.title.trim()) {
            newErrors.title = "Title is required";
        }

        if (!termsForm.content.trim()) {
            newErrors.content = "Content is required";
        }

        if (!termsForm.financialYear) {
            newErrors.financialYear = "Financial Year is required";
        }

        if (!termsForm.emData.emId) {
            newErrors.emData = "Educational Manager selection is required";
        }

        if (!termsForm.effectiveDate) {
            newErrors.effectiveDate = "Effective Date is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            toast.error("Please fill in all required fields");
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch(
                `${SERVER_ORIGIN}/api/terms/auth/update/${id}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": sessionStorage.getItem("token"),
                    },
                    body: JSON.stringify(termsForm),
                }
            );

            const result = await response.json();

            if (response.status >= 400) {
                toast.error(result.statusText || "Failed to update Terms and Conditions");
            } else {
                toast.success("Terms and Conditions updated successfully");
                navigate("/terms-conditions/all");
            }
        } catch (err) {
            toast.error("Error updating Terms and Conditions");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate("/terms-conditions/all");
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className={css.outerDiv}>
            <SecCard>
                <BackButton text="Back to Terms & Conditions" customPath="/terms-conditions/all" />
                <h2 className="text-ff1">Edit Terms and Conditions</h2>

                <div className="text-ff2" autoComplete="on">
                    <div className="row">
                        <div className="col-md-6">
                            <div style={{ marginBottom: "0.8rem" }}>
                                <label className={css.instiLabel} htmlFor="title">
                                    Title <span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    className={css.instiInput}
                                    type="text"
                                    id="title"
                                    name="title"
                                    placeholder="Enter Terms and Conditions Title"
                                    value={termsForm.title}
                                    onChange={handleInputChange}
                                    maxLength={200}
                                />
                                {errors.title && <small className="text-danger">{errors.title}</small>}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div style={{ marginBottom: "0.8rem" }}>
                                <label className={css.instiLabel} htmlFor="version">
                                    Version <span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    className={css.instiInput}
                                    type="text"
                                    id="version"
                                    name="version"
                                    placeholder="e.g., 1.0, 2.1"
                                    value={termsForm.version}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <div style={{ marginBottom: "0.8rem" }}>
                                <label className={css.instiLabel}>
                                    Financial Year <span style={{ color: "red" }}>*</span>
                                </label>
                                <Select
                                    options={financialYearOptions}
                                    value={financialYearOptions.find(option => option.value === termsForm.financialYear)}
                                    onChange={handleFinancialYearChange}
                                    placeholder="Select Financial Year"
                                    isClearable
                                />
                                {errors.financialYear && <small className="text-danger">{errors.financialYear}</small>}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div style={{ marginBottom: "0.8rem" }}>
                                <label className={css.instiLabel}>
                                    Educational Manager <span style={{ color: "red" }}>*</span>
                                </label>
                                <AsyncSelect
                                    cacheOptions
                                    loadOptions={loadEMOptions}
                                    defaultOptions
                                    value={selectedEM}
                                    onChange={handleEMChange}
                                    placeholder="Search and select Educational Manager"
                                    isClearable
                                />
                                {errors.emData && <small className="text-danger">{errors.emData}</small>}
                            </div>
                        </div>
                    </div>

                    {selectedEM && (
                        <div className="row">
                            <div className="col-md-4">
                                <div style={{ marginBottom: "0.8rem" }}>
                                    <label className={css.instiLabel}>Chapter Name</label>
                                    <input
                                        className={css.instiInput}
                                        type="text"
                                        value={termsForm.emData.chapterName}
                                        readOnly
                                        style={{ backgroundColor: '#f8f9fa' }}
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div style={{ marginBottom: "0.8rem" }}>
                                    <label className={css.instiLabel}>Region Name</label>
                                    <input
                                        className={css.instiInput}
                                        type="text"
                                        value={termsForm.emData.regionName}
                                        readOnly
                                        style={{ backgroundColor: '#f8f9fa' }}
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div style={{ marginBottom: "0.8rem" }}>
                                    <label className={css.instiLabel}>EM Name</label>
                                    <input
                                        className={css.instiInput}
                                        type="text"
                                        value={termsForm.emData.emName}
                                        readOnly
                                        style={{ backgroundColor: '#f8f9fa' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    <div style={{ marginBottom: "0.8rem" }}>
                        <label className={css.instiLabel} htmlFor="content">
                            Content <span style={{ color: "red" }}>*</span>
                        </label>
                        <textarea
                            className={css.instiInput}
                            id="content"
                            name="content"
                            placeholder="Enter Terms and Conditions content..."
                            rows="8"
                            value={termsForm.content}
                            onChange={handleInputChange}
                            style={{ resize: 'vertical' }}
                        />
                        {errors.content && <small className="text-danger">{errors.content}</small>}
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <div style={{ marginBottom: "0.8rem" }}>
                                <label className={css.instiLabel} htmlFor="effectiveDate">
                                    Effective Date <span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    className={css.instiInput}
                                    type="date"
                                    id="effectiveDate"
                                    name="effectiveDate"
                                    value={termsForm.effectiveDate}
                                    onChange={handleInputChange}
                                />
                                {errors.effectiveDate && <small className="text-danger">{errors.effectiveDate}</small>}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div style={{ marginBottom: "0.8rem" }}>
                                <label className={css.instiLabel} htmlFor="expiryDate">
                                    Expiry Date (Optional)
                                </label>
                                <input
                                    className={css.instiInput}
                                    type="date"
                                    id="expiryDate"
                                    name="expiryDate"
                                    value={termsForm.expiryDate}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <div style={{ marginBottom: "0.8rem" }}>
                                <label className={css.instiLabel}>
                                    Applies To
                                </label>
                                <Select
                                    isMulti
                                    options={appliesToOptions}
                                    value={appliesToOptions.filter(option => 
                                        termsForm.appliesTo.includes(option.value)
                                    )}
                                    onChange={handleAppliesToChange}
                                    placeholder="Select who these terms apply to"
                                />
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div style={{ marginBottom: "0.8rem" }}>
                                <label className={css.instiLabel} htmlFor="tags">
                                    Tags (Optional)
                                </label>
                                <input
                                    className={css.instiInput}
                                    type="text"
                                    id="tags"
                                    value={termsForm.tags.join(', ')}
                                    placeholder="Enter tags separated by commas"
                                    onChange={handleTagsChange}
                                />
                                <small className="text-muted">Example: privacy, data-protection, user-agreement</small>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end gap-3 mt-4">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Updating..." : "Update Terms and Conditions"}
                        </button>
                    </div>
                </div>
            </SecCard>
        </div>
    );
};

export default EditTermsAndConditions; 